import type { FieldValues, Path } from 'react-hook-form';

// Type definition for dropdown options.
import type { Option } from '../../../../components/elements/SingleChoiceDropdown/SingleChoiceDropdown';
// Type definition for width prop.
import type { WidthProp } from '../../../../types/common-props';
// Shared props type provided by the Controller wrapper (BaseFormField).
import type { ControlledFieldProps } from '../FormField/FormField';
// Re-uses the standard FormSelectInput component for display and RHF integration.
import { FormSelectInput } from '../FormSelectInput/FormSelectInput';

/** Base configuration props specific to the Query Select component. */
export interface BaseFormQuerySelectProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
  /** Text label displayed for the input. */
  label: string;
  /** A synchronous function that returns the array of options. */
  queryAndParseOptions: () => Option[];
  /** Indicates if the field is mandatory. */
  isRequired?: boolean;
  /** Specifies the CSS width of the component. */
  width?: WidthProp;
  /** Data test ID for automated testing. */
  dataTestId?: string;
}

/** Combined props for the FormQuerySelect component. */
interface FormQuerySelectProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>
  extends BaseFormQuerySelectProps<TFieldValues>,
    ControlledFieldProps<TFieldValues, TName> {
  // Includes field, errorMessage, isDisabled
}

/**
 * A specialized select input that fetches its options synchronously via a provided
 * query function immediately upon rendering. It then delegates the actual rendering
 * and React Hook Form integration to the more general `FormSelectInput` component.
 */
export function FormQuerySelect<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  queryAndParseOptions, // Specific function for this component.
  field, // RHF field object passed down from Controller wrapper.
  ...otherProps // Collects remaining props (name, label, error, disabled, etc.).
}: FormQuerySelectProps<TFieldValues, TName>) {
  // Execute the provided function immediately to get the options.
  const options = queryAndParseOptions();

  // Render the standard FormSelectInput component.
  return (
    <FormSelectInput
      // Pass the explicitly destructured 'field' prop.
      field={field}
      // Adapt the getOptions prop using the fetched options.
      getOptions={() => options}
      // Spread the remaining props (like name, label, errorMessage, isDisabled, width, isRequired).
      {...otherProps}
    />
  );
}
